import React, { useState, useEffect } from 'react';
import { MessageSquare, Clock, CheckCircle, Send, X } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : '/api');

export default function AdminSupportPanel() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/tickets/admin/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al obtener los tickets');
      const data = await res.json();
      setTickets(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const handleReplyTicket = async (e, closeTicket = false) => {
    e.preventDefault();
    if (!replyMessage.trim() && !closeTicket) return;
    
    try {
      const token = localStorage.getItem('token');
      const statusToSet = closeTicket ? 'resolved' : 'open';
      
      const res = await fetch(`${API_URL}/tickets/admin/${selectedTicket.id}/reply`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ message: replyMessage, status: statusToSet })
      });

      if (!res.ok) throw new Error('Error al responder al ticket');
      
      const updated = await res.json();
      setSelectedTicket(updated);
      setTickets(tickets.map(t => t.id === updated.id ? updated : t));
      setReplyMessage('');
      
      if (closeTicket) {
        setIsModalOpen(false);
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const openTicket = (ticket) => {
    setSelectedTicket(ticket);
    setIsModalOpen(true);
    setReplyMessage('');
  };

  if (loading) return <div style={{ color: 'var(--text-muted)', padding: '1rem' }}>Cargando tickets de soporte...</div>;
  if (error) return <div style={{ color: '#ef4444', padding: '1rem' }}>Error: {error}</div>;

  const openTicketsCount = tickets.filter(t => t.status === 'open').length;

  return (
    <div className="glass-panel" style={{ padding: '0', overflow: 'hidden', marginBottom: '2rem' }}>
      <div style={{ padding: '1.5rem', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 className="text-lg" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <MessageSquare size={20} color="#8b5cf6" />
          Centro de Soporte
          {openTicketsCount > 0 && (
            <span style={{ background: '#ef4444', color: 'white', fontSize: '0.75rem', padding: '0.2rem 0.6rem', borderRadius: '12px', marginLeft: '0.5rem' }}>
              {openTicketsCount} pendientes
            </span>
          )}
        </h3>
        <button onClick={fetchTickets} style={{ background: 'transparent', color: 'var(--health-cyan)', border: '1px solid var(--health-cyan)', padding: '0.4rem 0.8rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}>
          Actualizar
        </button>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.9rem' }}>
          <thead>
            <tr style={{ background: 'rgba(255,255,255,0.02)', color: 'var(--text-muted)' }}>
              <th style={{ padding: '1rem 1.5rem', fontWeight: 500 }}>Estado</th>
              <th style={{ padding: '1rem 1.5rem', fontWeight: 500 }}>Usuario</th>
              <th style={{ padding: '1rem 1.5rem', fontWeight: 500 }}>Asunto</th>
              <th style={{ padding: '1rem 1.5rem', fontWeight: 500 }}>Fecha</th>
              <th style={{ padding: '1rem 1.5rem', fontWeight: 500, textAlign: 'right' }}>Acción</th>
            </tr>
          </thead>
          <tbody>
            {tickets.length === 0 ? (
              <tr><td colSpan="5" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>No hay tickets en el sistema.</td></tr>
            ) : tickets.map((t, i) => (
              <tr key={t.id} style={{ borderTop: '1px solid rgba(255,255,255,0.05)', backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                <td style={{ padding: '1rem 1.5rem' }}>
                  <span style={{ 
                    padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                    background: t.status === 'open' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(16, 185, 129, 0.2)',
                    color: t.status === 'open' ? '#f59e0b' : '#10b981'
                  }}>
                    {t.status === 'open' ? <Clock size={12} /> : <CheckCircle size={12} />}
                    {t.status === 'open' ? 'ABIERTO' : 'RESUELTO'}
                  </span>
                </td>
                <td style={{ padding: '1rem 1.5rem' }}>
                  <div style={{ color: 'white', fontWeight: 500 }}>{t.user?.name || 'Usuario'}</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{t.user?.email || 'Desconocido'}</div>
                </td>
                <td style={{ padding: '1rem 1.5rem', color: 'white' }}>{t.subject}</td>
                <td style={{ padding: '1rem 1.5rem', color: 'var(--text-secondary)' }}>{new Date(t.createdAt).toLocaleDateString()}</td>
                <td style={{ padding: '1rem 1.5rem', textAlign: 'right' }}>
                  <button onClick={() => openTicket(t)} style={{ background: '#8b5cf6', color: 'white', border: 'none', padding: '0.4rem 0.8rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}>
                    Ver Ticket
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isModalOpen && selectedTicket && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(5px)',
          display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 100000
        }}>
          <div className="glass-panel" style={{ width: '90%', maxWidth: '700px', padding: '2rem', display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', borderBottom: '1px solid var(--glass-border)', paddingBottom: '1rem' }}>
              <div>
                <h2 style={{ margin: '0 0 0.5rem 0', color: 'white' }}>{selectedTicket.subject}</h2>
                <div style={{ display: 'flex', gap: '1rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                  <span>De: {selectedTicket.user?.name} ({selectedTicket.user?.email})</span>
                  <span>|</span>
                  <span>Fecha: {new Date(selectedTicket.createdAt).toLocaleString()}</span>
                </div>
              </div>
              <button onClick={() => setIsModalOpen(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1rem', paddingRight: '0.5rem', marginBottom: '1rem' }}>
              {/* Mensaje Original */}
              <div style={{ background: 'rgba(255,255,255,0.05)', padding: '1rem', borderRadius: '8px', alignSelf: 'flex-start', maxWidth: '85%' }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: '0.3rem' }}>{selectedTicket.user?.name}</span>
                <p style={{ margin: 0, color: 'white', whiteSpace: 'pre-wrap', lineHeight: '1.4' }}>{selectedTicket.message}</p>
              </div>

              {/* Respuestas */}
              {Array.isArray(selectedTicket.responses) ? selectedTicket.responses.map((r, i) => (
                <div key={i} style={{ 
                  background: r.isAdmin ? 'rgba(139, 92, 246, 0.15)' : 'rgba(255,255,255,0.05)', 
                  border: r.isAdmin ? '1px solid rgba(139, 92, 246, 0.3)' : 'none',
                  padding: '1rem', borderRadius: '8px', 
                  alignSelf: r.isAdmin ? 'flex-end' : 'flex-start', maxWidth: '85%' 
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                    <span style={{ fontSize: '0.8rem', color: r.isAdmin ? '#c4b5fd' : 'var(--text-muted)', fontWeight: r.isAdmin ? 'bold' : 'normal' }}>
                      {r.isAdmin ? 'Tú (Soporte)' : selectedTicket.user?.name}
                    </span>
                    <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{new Date(r.createdAt).toLocaleString()}</span>
                  </div>
                  <p style={{ margin: 0, color: 'white', whiteSpace: 'pre-wrap', lineHeight: '1.4' }}>{r.message}</p>
                </div>
              )) : (selectedTicket.responses ? JSON.parse(selectedTicket.responses).map((r, i) => (
                <div key={i} style={{ 
                  background: r.isAdmin ? 'rgba(139, 92, 246, 0.15)' : 'rgba(255,255,255,0.05)', 
                  border: r.isAdmin ? '1px solid rgba(139, 92, 246, 0.3)' : 'none',
                  padding: '1rem', borderRadius: '8px', 
                  alignSelf: r.isAdmin ? 'flex-end' : 'flex-start', maxWidth: '85%' 
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                    <span style={{ fontSize: '0.8rem', color: r.isAdmin ? '#c4b5fd' : 'var(--text-muted)', fontWeight: r.isAdmin ? 'bold' : 'normal' }}>
                      {r.isAdmin ? 'Tú (Soporte)' : selectedTicket.user?.name}
                    </span>
                    <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{new Date(r.createdAt).toLocaleString()}</span>
                  </div>
                  <p style={{ margin: 0, color: 'white', whiteSpace: 'pre-wrap', lineHeight: '1.4' }}>{r.message}</p>
                </div>
              )) : null)}
            </div>

            {selectedTicket.status === 'open' ? (
              <div style={{ borderTop: '1px solid var(--glass-border)', paddingTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <textarea 
                  value={replyMessage}
                  onChange={e => setReplyMessage(e.target.value)}
                  placeholder="Escribe una respuesta para el usuario..."
                  style={{ width: '100%', padding: '1rem', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--glass-border)', color: 'white', borderRadius: '8px', minHeight: '100px', resize: 'vertical' }}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                  <button 
                    onClick={(e) => handleReplyTicket(e, true)} 
                    style={{ background: 'transparent', color: '#10b981', border: '1px solid #10b981', padding: '0.8rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}
                  >
                    Cerrar Ticket
                  </button>
                  <button 
                    onClick={(e) => handleReplyTicket(e, false)} 
                    disabled={!replyMessage.trim()}
                    style={{ background: '#8b5cf6', color: 'white', border: 'none', padding: '0.8rem 1.5rem', borderRadius: '8px', cursor: !replyMessage.trim() ? 'not-allowed' : 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                  >
                    <Send size={18} /> Responder
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ padding: '1rem', textAlign: 'center', background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', borderRadius: '8px', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                Este ticket fue marcado como resuelto.
                <button 
                  onClick={(e) => { setReplyMessage('Ticket reabierto por el administrador.'); handleReplyTicket(e, false); }} 
                  style={{ display: 'block', margin: '1rem auto 0 auto', background: 'transparent', color: '#8b5cf6', border: '1px solid #8b5cf6', padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer' }}
                >
                  Reabrir Ticket
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
